import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  eyebrow?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  eyebrow,
  children,
  footer
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);
  return createPortal(<AnimatePresence data-id="element-189" data-mp="3uhgwpihjzhft">
      {open ? <div className="fixed inset-0 z-50 flex items-end justify-end sm:items-stretch" data-id="element-190" data-mp="3ugfij558qetv">
          <motion.div className="absolute inset-0 bg-navy-900/45" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} onClick={onClose} aria-hidden="true" data-id="element-191" data-mp="38te3aap68by" />
          <motion.aside ref={panelRef} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} initial={{
        opacity: 0,
        y: 32
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 24
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="relative flex max-h-[88vh] w-full flex-col rounded-t-2xl bg-white shadow-panel outline-none sm:max-h-none sm:h-full sm:w-[30rem] sm:rounded-none" data-id="element-192" data-mp="3bs0w6v5vboze">
            <div className="flex items-start gap-4 border-b border-navy-100 px-5 py-4" data-id="element-193" data-mp="2py3i2n9zxpin">
              <div className="min-w-0 flex-1" data-id="element-194" data-mp="2pvqwm0514x0u">
                {eyebrow ? <p className="text-xs font-semibold uppercase tracking-wide text-brand-600" data-id="element-195" data-mp="2kyqq5o49ps97">
                    {eyebrow}
                  </p> : null}
                <h2 className="mt-0.5 text-base font-semibold text-navy-900" data-id="element-196" data-mp="2uq4t65vfha73">
                  {title}
                </h2>
                {subtitle ? <p className="mt-1 text-sm text-navy-500" data-id="element-197" data-mp="2206264xob2zs">{subtitle}</p> : null}
              </div>
              <button type="button" onClick={onClose} aria-label="Close panel" className="-mr-1 -mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg text-navy-400 hover:bg-navy-50 hover:text-navy-700" data-id="element-198" data-mp="2gxyfcqfqzfat">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-199" data-mp="3ne0mr1ppalzn" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4" data-id="element-200" data-mp="3uwgycjh875ip">{children}</div>
            {footer ? <div className="border-t border-navy-100 px-5 py-4" data-id="element-201" data-mp="3qz1phnpci6c">{footer}</div> : null}
          </motion.aside>
        </div> : null}
    </AnimatePresence>, document.body);
}